import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'semantic-ui-react'
import Layout from '~/components/Layout'
import TableComponent from '~/components/TableComponent'
import { ManagerQuery } from '~/QueryHooks/ManagerQuery'
import { Nullable } from '~/types'

type DataFilterType = {
  rows: number,
  page: number,
  month: string
  farmerId: string
}

const ManagerQualities: React.FC = () => {
  const { t } = useTranslation()
  const tableColumnHeaders = [
    {
      name: t('Sampling time'),
      key: 'date',
      type: 'date',
      format: 'DD.MM.YYYY'
    },
    {
      name: t('Fat (in %)'),
      key: 'fat',
      type: 'no-unit-number'
    },
    {
      name: t('Protein (in %)'),
      key: 'protein',
      type: 'no-unit-number'
    },
    {
      name: t('Germ count (1000/ml)'),
      key: 'germCount',
      type: 'no-unit-number',
      decimalScale: 0
    },
    {
      name: t('Cell count (1000/ml)'),
      key: 'cellCount',
      type: 'no-unit-number',
      decimalScale: 0
    },
    {
      name: t('Inhibitor'),
      key: 'inhibitor'
    },
    {
      name: t('Urea (mg/dl)'),
      key: 'urea',
      type: 'no-unit-number'
    },
    {
      name: t('Freezing point (in °C)'),
      key: 'freezingPoint',
      type: 'no-unit-number'
    }
  ]

  const [dataFiltered, setDataFiltered] = useState<Nullable<DataFilterType>>(null)

  const {
    data: options,
    isLoading: isLoadingOptions
  } = ManagerQuery.getMilkTestsIndexData()

  const {
    data: qualitiesData,
    isLoading: isLoadingList,
    isFetching: isFetchingList,
    refetch: fetchData
  } = ManagerQuery.getMilkTestsIndex(dataFiltered && {
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  })

  useEffect(() => {
    if (!dataFiltered && options?.months?.length) {
      setDataFiltered({
        rows: 20,
        page: 0,
        month: options.months[0].key,
        farmerId: options?.farmers[0]?.id
      })
    }
  }, [options, dataFiltered])

  useEffect(() => {
    if (dataFiltered) {
      fetchData()
    }
  }, [dataFiltered])

  function renderFilters() {
    return <>
      <Dropdown
        selectOnBlur={false}
        placeholder={t('Select date')}
        selection
        options={options?.months?.map(month => ({ text: month.label, value: month.key })) || []}
        value={dataFiltered?.month}
        onChange={(_, { value }) => {
          setDataFiltered({
            ...dataFiltered,
            month: value as string
          })
        }}
      />
    </>
  }

  function renderTable() {
    return <TableComponent
      rows={rows}
      total={filtered}
      tableColumnHeaders={tableColumnHeaders}
      isFetchingData={isFetchingList}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
      animated
    />
  }

  function renderActions() {
    return <>
      <Dropdown
        selection
        options={options?.farmers?.map(farmer => ({ value: farmer.id, text: farmer.companyName }))}
        value={dataFiltered?.farmerId}
        onChange={(_, { value }) => {
          setDataFiltered({
            ...dataFiltered,
            farmerId: value as string
          })
        }}
        placeholder='Betrieb wählen'
      />
    </>
  }

  const { filtered, rows } = qualitiesData ? qualitiesData : { filtered: 0, rows: [] }
  const isLoading = isLoadingList || isLoadingOptions

  return <Layout
    title={t('Qualities')}
    segmented
    isBusy={isLoading}
    filters={renderFilters()}
    actions={renderActions()}
  >
    {dataFiltered && qualitiesData && renderTable()}
  </Layout>
}

export default ManagerQualities
